/**
 * Clés TanStack Query, centralisées.
 *
 * Éparpiller les clés dans les composants rend l'invalidation approximative :
 * on finit par invalider trop (rechargements inutiles) ou trop peu (données
 * périmées à l'écran).
 */
export const metaKeys = {
  all: ["meta"] as const,
} as const;

export const authKeys = {
  all: ["auth"] as const,
  me: () => [...authKeys.all, "me"] as const,
  sessions: () => [...authKeys.all, "sessions"] as const,
} as const;

/**
 * Compte de l'utilisateur courant.
 *
 * Distinct de `authKeys.me()`, qui porte la vue de session (rôles, permissions) :
 * renommer son compte doit rafraîchir les deux, et les confondre ferait
 * réafficher un nom périmé dans la barre de navigation.
 */
export const accountKeys = {
  all: ["account"] as const,
  me: () => [...accountKeys.all, "me"] as const,
  profile: () => [...accountKeys.all, "profile"] as const,
  settings: () => [...accountKeys.all, "settings"] as const,
} as const;

/**
 * Catalogue de cours (docs/11).
 *
 * `allCourses()` est le préfixe **commun** au catalogue public et à la liste de
 * l'enseignant : publier un cours change les deux, et n'invalider que celle
 * qu'on a sous les yeux laisserait l'autre afficher un statut périmé.
 *
 * La fiche est indexée deux fois — par identifiant et par slug — parce que les
 * deux routes existent (`/courses/{id}` pour l'espace de travail, `by-slug`
 * pour les URL partageables) et qu'elles ne rendent pas la même clé de cache.
 */
export const catalogKeys = {
  all: ["catalog"] as const,
  domains: () => [...catalogKeys.all, "domains"] as const,
  allCourses: () => [...catalogKeys.all, "courses"] as const,
  catalog: (filters: Record<string, string | undefined>) =>
    [...catalogKeys.allCourses(), "public", filters] as const,
  // L'autocomplétion est sous `allCourses()` : publier ou archiver un cours
  // change ce que la boîte de saisie doit proposer, et c'est exactement ce que
  // `useAfterCourseChange` invalide déjà.
  suggestions: (text: string) => [...catalogKeys.allCourses(), "suggest", text] as const,
  mine: (filters: Record<string, string | undefined>) =>
    [...catalogKeys.allCourses(), "mine", filters] as const,
  course: (courseId: string) => [...catalogKeys.allCourses(), "detail", courseId] as const,
  courseBySlug: (slug: string) => [...catalogKeys.allCourses(), "slug", slug] as const,
  lesson: (courseId: string, lessonId: string) =>
    [...catalogKeys.course(courseId), "lesson", lessonId] as const,
  // La prévisualisation est sous le cours et non sous la leçon : elle change
  // dès qu'une question bouge, et l'invalider avec la leçon la laisserait
  // afficher l'exercice d'avant la dernière modification.
  exercisePreview: (courseId: string, exerciseId: string) =>
    [...catalogKeys.course(courseId), "exercise", exerciseId, "preview"] as const,
} as const;

/**
 * Progression de l'élève (docs/12).
 *
 * `player(courseId)` est indexé par **cours** et non par inscription : c'est le
 * cours que l'URL porte, et l'identifiant d'inscription change quand un élève se
 * réinscrit après s'être désinscrit (docs/12 §8) — une clé bâtie dessus
 * laisserait l'ancien plan en cache sous une clé que plus rien ne consulte.
 *
 * Toute écriture de progression invalide `all` : rendre une copie peut
 * débloquer une leçon, donc changer le player, la liste « mes cours » et l'état
 * de l'exercice à la fois. Invalider finement demanderait de rejouer côté client
 * la règle de déblocage — c'est-à-dire de la dupliquer.
 */
export const learningKeys = {
  all: ["learning"] as const,
  enrollments: () => [...learningKeys.all, "enrollments"] as const,
  dashboard: () => [...learningKeys.all, "dashboard"] as const,
  player: (courseId: string) => [...learningKeys.all, "player", courseId] as const,
  exercise: (exerciseId: string) => [...learningKeys.all, "exercise", exerciseId] as const,
  attempt: (attemptId: string) => [...learningKeys.all, "attempt", attemptId] as const,
  certificates: () => [...learningKeys.all, "certificates"] as const,
  /**
   * La vérification **publique** d'un numéro de série.
   *
   * Sous `learningKeys` par sujet, mais c'est la seule clé de l'application qui
   * puisse être remplie sans session : le cache d'un visiteur anonyme et celui
   * d'un élève connecté ne contiennent pas la même chose sous le même préfixe.
   * Ce n'est pas un problème ici — la réponse est la même pour tout le monde,
   * c'est précisément ce que « publique » veut dire — et ce serait un problème
   * partout ailleurs.
   */
  certificateCheck: (serial: string) => [...learningKeys.all, "certificate-check", serial] as const,
} as const;

/**
 * Ce que l'enseignant lit de ses élèves (docs/12 §4 et §6).
 *
 * Racine **distincte** de `learningKeys`, et ce n'est pas un rangement : les
 * deux préfixes sont invalidés ensemble après une notation — la note change la
 * file **et** le player de l'élève —, mais ils ne se vident pas ensemble à la
 * déconnexion d'un rôle, et surtout ils ne racontent pas la même chose. Les
 * confondre ferait recharger le parcours d'un élève à chaque rafraîchissement
 * d'un tableau de bord d'enseignant.
 *
 * `gradingQueue()` porte le filtre de cours dans sa clé : sans lui, passer d'un
 * cours à « tous » servirait la page précédente depuis le cache.
 */
export const teachingKeys = {
  all: ["teaching"] as const,
  dashboard: () => [...teachingKeys.all, "dashboard"] as const,
  gradingQueue: (courseId?: string) =>
    [...teachingKeys.all, "grading-queue", courseId ?? "all"] as const,
  submission: (attemptId: string) => [...teachingKeys.all, "submission", attemptId] as const,
  student: (studentId: string) => [...teachingKeys.all, "student", studentId] as const,
} as const;

/**
 * Offres et droits (docs/10 §9).
 *
 * `entitlements()` est la clé la plus invalidée du lot : elle change à chaque
 * souscription, changement de plan, annulation et octroi manuel. Elle est
 * **distincte** de `authKeys.me()` — les permissions viennent du jeton et ne
 * bougent qu'à la reconnexion, les droits sont lus en base à chaque appel, et
 * les confondre ferait recharger la session à chaque paiement.
 *
 * `plans()` ne porte pas l'identité de l'appelant alors que `is_current` en
 * dépend : c'est voulu, et c'est pourquoi une souscription invalide `all`.
 * Indexer par utilisateur ferait grossir le cache d'une entrée par visiteur
 * pour une liste de deux offres identiques à un booléen près.
 */
export const billingKeys = {
  all: ["billing"] as const,
  plans: () => [...billingKeys.all, "plans"] as const,
  entitlements: () => [...billingKeys.all, "entitlements"] as const,
  subscription: () => [...billingKeys.all, "subscription"] as const,
  usage: () => [...billingKeys.all, "usage"] as const,
  userEntitlements: (userId: string) => [...billingKeys.all, "user", userId] as const,
} as const;

/**
 * Back-office. `allUsers()` est le préfixe de toutes les listes filtrées :
 * après une suspension, on invalide le préfixe et non chaque combinaison de
 * filtres, qu'on ne peut pas énumérer.
 */
export const adminKeys = {
  all: ["admin"] as const,
  allUsers: () => [...adminKeys.all, "users"] as const,
  users: (filters: Record<string, string | undefined>) =>
    [...adminKeys.allUsers(), "list", filters] as const,
  user: (userId: string) => [...adminKeys.allUsers(), "detail", userId] as const,
  roles: () => [...adminKeys.all, "roles"] as const,
  auditLogs: () => [...adminKeys.all, "audit"] as const,
  auditLog: (action?: string) => [...adminKeys.auditLogs(), action ?? "all"] as const,
  // Les statistiques (docs/16 §8). `stats()` est le préfixe commun : les deux
  // lectures répondent à la même question posée à deux échelles de temps, et
  // rafraîchir l'une sans l'autre ferait un écran dont les chiffres et la
  // courbe ne datent pas du même instant.
  stats: () => [...adminKeys.all, "stats"] as const,
  statsOverview: () => [...adminKeys.stats(), "overview"] as const,
  // La fenêtre est dans la clé : passer de 7 à 30 jours servirait sinon la
  // courbe précédente depuis le cache, et le graphique changerait d'axe sans
  // changer de données.
  statsGrowth: (days: number) => [...adminKeys.stats(), "growth", days] as const,
  // Les feature flags (docs/05 §11). Une seule clé, sans filtre : la liste est
  // courte par construction — un flag par fonctionnalité qu'on a voulu pouvoir
  // couper — et une bascule invalide l'ensemble, l'écran affichant l'état
  // complet.
  featureFlags: () => [...adminKeys.all, "feature-flags"] as const,
  // La vue de l'outbox porte son filtre dans la clé : passer de « en échec » à
  // « en attente » servirait sinon la page précédente depuis le cache, et
  // l'opérateur croirait que le rejeu n'a rien changé. Un rejeu invalide
  // `outbox()` en entier, l'événement changeant de vue.
  outbox: () => [...adminKeys.all, "outbox"] as const,
  outboxView: (view: string) => [...adminKeys.outbox(), view] as const,
  // La file de modération (phase 11). `reports()` est invalidé en bloc après
  // une décision : trancher fait quitter la file au dossier, et son statut
  // change dans la fiche comme dans la liste.
  //
  // `reportContent(id)` est **à part et n'est jamais invalidé** : le contenu
  // divulgué est le résultat d'un geste explicite, écrit au journal d'audit.
  // Le recharger tout seul écrirait une seconde consultation que personne n'a
  // demandée — un journal d'accès ne doit compter que des gestes humains.
  reports: () => [...adminKeys.all, "reports"] as const,
  reportsView: (openOnly: boolean) => [...adminKeys.reports(), openOnly] as const,
  report: (reportId: string) => [...adminKeys.reports(), "detail", reportId] as const,
  reportContent: (reportId: string) => [...adminKeys.reports(), "content", reportId] as const,
} as const;

/**
 * Fichiers téléversés (docs/09).
 *
 * `policy(purpose)` porte l'usage dans sa clé : les types acceptés diffèrent
 * d'un usage à l'autre — un PDF pour une pièce jointe de leçon, jamais pour une
 * couverture — et une clé commune servirait la mauvaise liste au second
 * formulaire ouvert.
 *
 * Toute écriture invalide `all` **et** `billingKeys.usage()` : un dépôt change
 * la liste des fichiers et le nombre de mébioctets occupés, que l'écran de
 * l'offre affiche. Les invalider séparément laisserait l'un des deux mentir, et
 * c'est le compteur qu'on regarde justement pour savoir s'il reste de la place.
 */
export const storageKeys = {
  all: ["storage"] as const,
  list: (purpose?: string) => [...storageKeys.all, "list", purpose ?? "all"] as const,
  policy: (purpose: string) => [...storageKeys.all, "policy", purpose] as const,
} as const;

/**
 * Centre de notifications (docs/14 §8).
 *
 * `all` est invalidé en bloc par le flux SSE et par chaque geste de lecture :
 * marquer une notification lue change la liste **et** le badge, et les invalider
 * séparément laisserait le compteur mentir — or c'est précisément le chiffre
 * qu'on regarde pour savoir s'il reste quelque chose à lire.
 *
 * `preferences()` vit sous le même préfixe sans en subir les invalidations
 * utiles : elle ne change qu'à l'enregistrement du formulaire, et sa mutation
 * réécrit le cache avec la réponse plutôt que de redemander.
 */
export const notificationKeys = {
  all: ["notifications"] as const,
  list: (unreadOnly: boolean) => [...notificationKeys.all, "list", unreadOnly] as const,
  unreadCount: () => [...notificationKeys.all, "unread-count"] as const,
  preferences: () => [...notificationKeys.all, "preferences"] as const,
} as const;

/**
 * Messagerie (docs/13).
 *
 * `all` est invalidé en bloc par le flux SSE et par chaque envoi : un message
 * change la liste des conversations, son compteur de non-lus et le badge
 * global. Les invalider séparément laisserait l'un des trois mentir — et c'est
 * le badge qu'on regarde pour savoir s'il reste quelque chose à lire.
 *
 * `messages(conversationId)` est **à part** parce qu'elle seule est paginée :
 * l'invalider recharge un historique qui peut faire plusieurs pages, et on ne
 * le fait donc que pour la conversation réellement concernée par la trame.
 *
 * `list(includeArchived)` porte son filtre dans la clé : sans lui, cocher
 * « voir les archivées » servirait la liste précédente depuis le cache, et
 * l'utilisateur croirait n'avoir rien archivé.
 */
export const messagingKeys = {
  all: ["messaging"] as const,
  list: (includeArchived: boolean) =>
    [...messagingKeys.all, "conversations", includeArchived] as const,
  conversation: (conversationId: string) =>
    [...messagingKeys.all, "conversation", conversationId] as const,
  // Deux clés et non une : `messagesOf` est le **préfixe** de toutes les
  // lectures d'un fil, `messages` en désigne une — celle d'une recherche
  // donnée, la chaîne vide valant « pas de recherche ». Deux saisies sont deux
  // résultats, et les servir depuis la même entrée ferait clignoter le fil à
  // chaque lettre ; mais une invalidation — celle du flux SSE, celle d'un
  // envoi — doit les emporter **toutes**, et c'est le préfixe qui le fait.
  // Invalider `messages(id)` n'emporterait que la vue non filtrée, et la
  // recherche affichée à l'écran resterait sur des messages périmés.
  messagesOf: (conversationId: string) =>
    [...messagingKeys.all, "messages", conversationId] as const,
  messages: (conversationId: string, search = "") =>
    [...messagingKeys.messagesOf(conversationId), search] as const,
  unreadCount: () => [...messagingKeys.all, "unread-count"] as const,
} as const;
